import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router-dom';
import { intl } from 'i18n';

import { CurriculumsApi, ItemsApi } from 'global/api';
import { CurriculumDetail } from 'labxchange-client';
import { Button, PillButton, showErrorMessage } from 'ui/components';
import {
  WrappedMessage,
  getHomeVariationExperimentValue,
  isLocalOrReviewApp,
} from 'utils';
import { ROUTES } from 'global/constants';
import { analyticsInstance, EVENT_NAMES } from 'tracking';
import uiMessages from 'ui/components/displayMessages';

import messages from '../../displayMessages';
import HeroContentHeading from './HeroContentHeading';
import CommonContent from './CommonContent';
import { PlainAssetCard, PlainAssetCardSkeleton } from './PlainAssetCard';

// Add mappings here for curriculum
export const CURRICULUM_ASSET_MAPPINGS: any = {
  ncert: {
    biology: {
      'class-xi': [
        'lb:LabXchange:a5438ebc:html:1',
        'lb:LabXchange:21c1cb80-e0c2-3c8b-ae10-f6775e43b81f:html:1',
        'lb:LabXchange:8e3b1a74:video:1',
        'lb:LabXchange:085f8e15:video:1',
        'lb:LabXchange:3a4d2342:video:1',
        'lb:LabXchange:7e57d034:lx_simulation:1',
        'lb:LabXchange:fbdb87f4:lx_simulation:1',
        'lb:LabXchange:6bf1c86d:lx_teaching_guide:1',
      ],
      'class-xii': [
        'lb:LabXchange:c9d6a366:lx_assignment:1',
        'lb:LabXchange:5ac78a28:html:1',
        'lb:LabXchange:b616dadc:video:1',
        'lb:LabXchange:9eaf0527:lx_image:1',
        'lb:LabXchange:95bb2d4e:lx_case_study:1',
        'lb:LabXchange:63ee7b85:lx_teaching_guide:1',
        'lb:LabXchange:1fb31712:lx_simulation:1',
        'lb:LabXchange:7c77110e:lx_simulation:1',
      ],
    },
    chemistry: {
      'class-xi': [
        'lb:LabXchange:46aeb0dc:html:1',
        'lb:LabXchange:Wz9TCV4Z:html:1',
        'lb:LabXchange:d4da90e1:video:1',
        'lb:LabXchange:8832163b:video:1',
        'lb:LabXchange:0ee38d28:lx_simulation:1',
        'lb:LabXchange:e88b20ae:lx_teaching_guide:1',
        'lb:LabXchange:a6782bb7:lx_simulation:1',
        'lb:LabXchange:a763090f:problem:1',
      ],
      'class-xii': [
        'lb:LabXchange:38588b73:html:1',
        'lb:LabXchange:xjyUhQlx:html:1',
        'lb:LabXchange:95f38a53:video:1',
        'lb:LabXchange:3fd6fcc2:video:1',
        'lb:LabXchange:807e0eae:lx_simulation:1',
        'lb:LabXchange:626b1dac:lx_simulation:1',
        'lb:LabXchange:a023f1c1:lx_teaching_guide:1',
        'lb:LabXchange:13ae25a3:problem:1',
      ],
    },
  },
  caps: {
    'life-science': {
      'grade-10': [
        'lb:LabXchange:9967ebf1-f63d-3199-97b6-ec0c987edac5:html:1',
        'lb:LabXchange:32d11d13:video:1',
        'lb:LabXchange:391ece50:video:1',
        'lb:LabXchange:865c25a1:lx_image:1',
        'lb:LabXchange:556a5893:video:1',
        'lb:LabXchange:575225d6:lx_assignment:1',
        'lb:LabXchange:37f58b9c:lx_simulation:1',
        'lb:LabXchange:40cbbdba:lx_case_study:1',
      ],
      'grade-11': [
        'lb:LabXchange:7d9eb05f:html:1',
        'lb:LabXchange:ebc9cb58:html:1',
        'lb:LabXchange:b794a7e7:video:1',
        'lb:LabXchange:5863f45b:lx_image:1',
        'lb:LabXchange:b8855d07:video:1',
        'lb:LabXchange:06b930c2:lx_case_study:1',
        'lb:LabXchange:463da635:lx_simulation:1',
        'lb:LabXchange:566c86af:lx_teaching_guide:1',
      ],
      'grade-12': [
        'lb:LabXchange:336f49b9:video:1',
        'lb:LabXchange:8e8b1b1b:html:1',
        'lb:LabXchange:e4808f46-8abb-3e55-9921-60d4ebc7a753:html:1',
        'lb:LabXchange:389237bb:video:1',
        'lb:LabXchange:5c1562b9:lx_simulation:1',
        'lb:LabXchange:5e1fcef1:lx_simulation:1',
        'lb:LabXchange:06ff204b:lx_case_study:1',
        'lb:LabXchange:20522ed6:lx_teaching_guide:1',
      ],
    },
    'physical-science': {
      'grade-10': [
        'lb:LabXchange:65c0ccf6-bbd6-3531-b414-c0174b15fb7d:html:1',
        'lb:LabXchange:42f1836c:html:1',
        'lb:LabXchange:cf7b8140:lx_simulation:1',
        'lb:LabXchange:689939c8:video:1',
        'lb:LabXchange:5128ddc9:lx_simulation:1',
        'lb:LabXchange:f7a5eab0:video:1',
        'lb:LabXchange:3f28b23a:lx_case_study:1',
        'lb:LabXchange:d6f7151a:problem:1',
      ],
      'grade-11': [
        'lb:LabXchange:279ab9f1:video:1',
        'lb:LabXchange:8b62af9b:html:1',
        'lb:LabXchange:e5b207e4:video:1',
        'lb:LabXchange:de6eecef:html:1',
        'lb:LabXchange:8a898fbe:lx_teaching_guide:1',
        'lb:LabXchange:b9c8b7d7:lx_image:1',
        'lb:LabXchange:3ec14474:lx_simulation:1',
        'lb:LabXchange:fda8289f:problem:1',
      ],
      'grade-12': [
        'lb:LabXchange:2c0d1945:html:1',
        'lb:LabXchange:JEJNADzE:html:1',
        'lb:LabXchange:20349404:html:1',
        'lb:LabXchange:f17cdc0b:lx_assignment:1',
        'lb:LabXchange:67f0f475:video:1',
        'lb:LabXchange:c5c87faa:lx_image:1',
        'lb:LabXchange:312da9a0:lx_simulation:1',
        'lb:LabXchange:f8c13794:lx_teaching_guide:1',
      ],
    },
  },
  ngss: {
    'life-sciences': {
      'grades-9-12': [
        'lb:LabXchange:63ee7b85:lx_teaching_guide:1',
        'lb:LabXchange:8f469685-176b-3f82-9ff8-d58b8ca3f078:html:1',
        'lb:LabXchange:031a2942-c44b-3023-b0d1-500c800b1bb7:html:1',
        'lb:LabXchange:dad95647:lx_simulation:1',
        'lb:LabXchange:545f48fb:lx_image:1',
        'lb:LabXchange:6b7cad46:lx_teaching_guide:1',
        'lb:LabXchange:af55d8e2:video:1',
        'lb:LabXchange:339e77e8:lx_case_study:1',
      ],
    },
    'earth-and-space-science': {
      'grades-9-12': [
        'lb:LabXchange:9b219fb8:lx_teaching_guide:1',
        'lb:LabXchange:8bfe90b0-e5dd-3638-8149-9b7df062ddbf:html:1',
        'lb:LabXchange:f1694746:html:1',
        'lb:LabXchange:07b315c0:lx_simulation:1',
        'lb:LabXchange:30e06c39:lx_image:1',
        'lb:LabXchange:c2554926:video:1',
        'lb:LabXchange:656f7f76:video:1',
        'lb:LabXchange:05cbf29d:lx_case_study:1',
      ],
    },
    'physical-science': {
      'grades-9-12': [
        'lb:LabXchange:3f2b232b:lx_teaching_guide:1',
        'lb:LabXchange:fdbbf619-e569-3623-958d-4a743f15049a:html:1',
        'lb:LabXchange:05a8d27a-b80f-3d69-a19c-f28de0798de0:html:1',
        'lb:LabXchange:77e27ad7:lx_simulation:1',
        'lb:LabXchange:7fff6c97:lx_image:1',
        'lb:LabXchange:aebd0dc8:lx_simulation:1',
        'lb:LabXchange:d6f7151a:problem:1',
        'lb:LabXchange:84581d1c:video:1',
      ],
    },
  },
};

interface HomeVariationThreeProps {
  curriculumSlug: string;
  countryCode: string;
  isUserLoggedIn: boolean;
}

export const HomeVariationThree = ({
  curriculumSlug,
  isUserLoggedIn,
  countryCode,
}: HomeVariationThreeProps) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const experimentVariation = getHomeVariationExperimentValue(curriculumSlug);

  const [loading, setLoading] = useState<boolean>(true);
  const [curriculum, setCurriculum] = useState<CurriculumDetail | undefined>(
    undefined
  );
  const [assetsLoading, setAssetsLoading] = useState<boolean>(true);
  const [assets, setAssets] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>(
    queryParams.get('subject') || ''
  );
  const [selectedGrade, setSelectedGrade] = useState<string>(
    queryParams.get('grade') || ''
  );

  useEffect(() => {
    const fetchCurriculumData = async () => {
      setLoading(true);
      try {
        const response: CurriculumDetail = await CurriculumsApi.getDetail({
          id: curriculumSlug,
        });
        setCurriculum(response);
        const firstSubject = response.subjects?.[0];
        if (!selectedSubject) {
          setSelectedSubject(firstSubject?.slug || '');
        }
        if (!selectedGrade) {
          setSelectedGrade(firstSubject?.grades?.[0].slug || '');
        }
      } catch (error) {
        setLoading(false);
        setCurriculum(undefined);
        showErrorMessage(
          <WrappedMessage message={messages.homeVariationPageCurriculumError} />
        );
      }
      setLoading(false);
    };

    fetchCurriculumData();
  }, [curriculumSlug]);

  useEffect(() => {
    const fetchAssetsData = async () => {
      setAssetsLoading(true);
      setAssets([]);
      try {
        const assetIds = isLocalOrReviewApp()
          ? Array(8).fill('lb:LabXchange:1fb8b9d6:lx_simulation:2')
          : CURRICULUM_ASSET_MAPPINGS[curriculumSlug]?.[selectedSubject]?.[
              selectedGrade
            ];
        if (!assetIds) {
          setAssetsLoading(false);
          return;
        }

        const assetPromises = assetIds.map((assetId: string) =>
          ItemsApi.read({ id: assetId })
        );
        const assetsResponse = await Promise.all(assetPromises);

        setAssets(assetsResponse);
      } catch (error) {
        setAssetsLoading(false);
        setAssets([]);
        showErrorMessage(
          <WrappedMessage message={messages.homeVariationPageAssetsError} />
        );
      }
      setAssetsLoading(false);
    };

    fetchAssetsData();
  }, [selectedSubject, selectedGrade]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (selectedSubject) params.set('subject', selectedSubject);
    if (selectedGrade) params.set('grade', selectedGrade);
    window.history.replaceState(null, '', `?${params.toString()}`);
  }, [selectedSubject, selectedGrade]);

  const onPillClick = (subjectSlug: string, gradeSlug: string) => {
    setSelectedSubject(subjectSlug);
    setSelectedGrade(gradeSlug);
  };

  const renderHeroSection = () => (
    <div className='hc-var-two-hero-section'>
      <div className='bg-map' tabIndex={-1}>
        <img
          src={`/assets/images/home-bg-${curriculumSlug}-map.png`}
          alt={intl.formatMessage(messages.homeVariationPageBgMapAlt, {
            curriculumSlug,
          })}
        />
      </div>
      <div className='bg-flag' tabIndex={-1}>
        <img
          src={`/assets/images/home-bg-${curriculumSlug}-flag.png`}
          alt={intl.formatMessage(messages.homeVariationPageBgFlagAlt, {
            curriculumSlug,
          })}
        />
      </div>
      <div className='harvard-logo'>
        <img
          src='/assets/images/harvard-logo-white.svg'
          alt={intl.formatMessage(uiMessages.uiHarvardOnlineLogo)}
          title={intl.formatMessage(uiMessages.uiHarvardOnlineLogo)}
        />
      </div>
      <div className='hero-content-wrapper'>
        <div className='hero-content'>
          <HeroContentHeading countryCode={countryCode} />
          <div className='hero-content-pill-buttons'>
            {loading
              ? Array.from({ length: 4 }, (_, index) => (
                  <Skeleton
                    key={index}
                    className='pill-button'
                    width={150}
                    height={40}
                  />
                ))
              : curriculum?.subjects?.map((subject) =>
                  subject.grades?.map((grade) => {
                    if (subject.isPublic) {
                      const isSelected =
                        selectedSubject === subject.slug &&
                        selectedGrade === grade.slug;
                      return (
                        <PillButton
                          key={`${subject.slug}-${grade.slug}`}
                          label={`${subject.name} ${grade.name}`}
                          onClick={() => onPillClick(subject.slug, grade.slug)}
                          isSelected={isSelected}
                          ariaLabel={`${subject.name} ${grade.name}`}
                        />
                      );
                    }
                    return null;
                  })
                )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAssetCards = () => {
    return (
      <div className='hc-var-three-asset-cards-wrapper'>
        <div className='hc-var-three-asset-cards'>
          {assetsLoading
            ? Array.from({ length: 8 }, (_, index) => (
                <PlainAssetCardSkeleton key={index} />
              ))
            : assets.map((asset) => (
                <PlainAssetCard
                  key={asset.id}
                  asset={asset}
                  curriculumSlug={curriculumSlug}
                  trackingData={{
                    curriculum_slug: curriculumSlug,
                    country: countryCode,
                    experiment_variation: experimentVariation,
                  }}
                />
              ))}
        </div>
        <Button
          className='asset-cards-section-signup-btn'
          label={messages.homeVariationExploreAllTopicsBtnLabel}
          href={`${ROUTES.Curriculum.DETAIL_SLUG(curriculumSlug)}?subject=${selectedSubject}&grade=${selectedGrade}`}
          onClick={() => {
            analyticsInstance.track(
              EVENT_NAMES.NewHomeExploreAllTopicsButtonClicked(
                experimentVariation
              ),
              {
                curriculum_slug: curriculumSlug,
                country: countryCode,
                experiment_variation: experimentVariation,
                button_text: intl.formatMessage(
                  messages.homeVariationExploreAllTopicsBtnLabel
                ),
                url: window.location.toString(),
              }
            );
          }}
          icon='arrow-right-thin'
          iconPosition='right'
          btnStyle='unstyled'
          data-testid='explore-all-topics-btn'
        />
      </div>
    );
  };

  return (
    <>
      {renderHeroSection()}
      {renderAssetCards()}
      <CommonContent
        isUserLoggedIn={isUserLoggedIn}
        curriculumSlug={curriculumSlug}
        countryCode={countryCode}
      />
    </>
  );
};

export default HomeVariationThree;
